import { UseQueryResult } from "@tanstack/react-query";
import { API_TYPES, useGetQuery, UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { z } from "zod";
import { UserType } from "@src/appV2/ShiftBlock/types/user";

export const workerLeftEarlyIncludedShiftSchema = z.object({
  type: z.literal(API_TYPES.shift),
  id: z.string(),
  attributes: z.object({
    name: z.string().optional(),
    start: z.string().datetime(),
    end: z.string().datetime(),
    agentId: z.string().optional(),
    urgency: z.number().optional(),
    replacedShiftId: z.string().optional(),
  }),
});

export type WorkerLeftEarlyIncludedShiftType = z.infer<typeof workerLeftEarlyIncludedShiftSchema>;

export const workerLeftEarlyIncludedWorkerSchema = z.object({
  type: z.literal(API_TYPES.worker),
  id: z.string(),
  attributes: z.object({ name: z.string() }),
});

export type WorkerLeftEarlyIncludedWorkerType = z.infer<typeof workerLeftEarlyIncludedWorkerSchema>;
export const workerLeftEarlyResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workerLeftEarlyRequest),
    id: z.string(),
    attributes: z.object({
      shiftId: z.string(),
      workerId: z.string().optional(),
      replacementRequested: z.boolean(),
      leftWithPermission: z.boolean(),
      comment: z.string(),
      replacementShiftId: z.string().optional(),
      createdBy: z
        .object({
          userId: z.string().optional(),
          name: z.string().optional(),
          userType: z.nativeEnum(UserType).optional(),
        })
        .optional(),
    }),
    relationships: z
      .array(
        z.union([
          z.object({
            type: z.literal(API_TYPES.shift),
            id: z.string(),
          }),
          z.object({
            type: z.literal(API_TYPES.worker),
            id: z.string(),
          }),
        ])
      )
      .optional(),
  }),
  included: z
    .array(z.union([workerLeftEarlyIncludedShiftSchema, workerLeftEarlyIncludedWorkerSchema]))
    .optional(),
});

export type WorkerLeftEarlyResponse = z.infer<typeof workerLeftEarlyResponseSchema>;

export function useGetWorkerLeftEarly(
  workerLeftEarlyRequestId: string,
  options: UseGetQueryOptions<WorkerLeftEarlyResponse> = {}
): UseQueryResult<WorkerLeftEarlyResponse> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/worker-left-early-requests/${workerLeftEarlyRequestId}`,
    queryParams: {
      include: ["worker", "shift"],
    },
    responseSchema: workerLeftEarlyResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_LEFT_EARLY_FAILURE,
    },
    ...options,
  });
}
